import type { TotalSales } from "~/types/api/client/google/google";

export class googleAPI {

    clientId: number;
    propertyId: number

    constructor(clientId: number, propertyId: number) {
        this.clientId = clientId;
        this.propertyId = propertyId
    }

    // Converts a date string in YYYYMMDD format to a Date object
    private convertDateString(dateString: string): Date {
        const year = dateString.slice(0, 4);
        const month = dateString.slice(4, 6);
        const day = dateString.slice(6, 8);
        return new Date(`${year}-${month}-${day}`);
    }

    async getRevenue(date: number = 28): Promise<TotalSales> {
        // Query for the last 28 days
        const query1 = {
            property: `properties/${this.propertyId}`,
            dateRanges: [
                {
                    startDate: `${date}daysAgo`,
                    endDate: 'today',
                },
            ],
            dimensions: [
                {
                    name: 'date'
                },
                {
                    name: 'currencyCode'
                }
            ],
            metrics: [
                {
                    name: 'grossPurchaseRevenue',
                },
            ],
        }

        // Query for the previous 28 days (29-56 days ago)
        const query2 = {
            property: `properties/${this.propertyId}`,
            dateRanges: [
                {
                    startDate: `${date*2}daysAgo`,
                    endDate: `${date+1}daysAgo`,
                },
            ],
            dimensions: [
                {
                    name: 'date'
                },
                {
                    name: 'currencyCode'
                }
            ],
            metrics: [
                {
                    name: 'grossPurchaseRevenue',
                },
            ],
        }

        const myCookies = useRequestHeaders(['cookie'])

        // Fetch data for both queries concurrently
        const [initialQuery, historyData] = await Promise.all([
            $fetch('/api/client/google/google', {
                headers: myCookies,
                method: 'POST',
                body: JSON.stringify(query1)
            }),
            $fetch('/api/client/google/google', {
                headers: myCookies,
                method: 'POST',
                body: JSON.stringify(query2)
            })
        ]);

        // Process data for the last 28 days
        const initialData = initialQuery.rows.map(row => ({
            date: row.dimensionValues[0].value,
            sales: parseFloat(row.metricValues[0].value),
            currency: row.dimensionValues[1].value,
        }));
        const currency = initialQuery.rows[0].dimensionValues[1].value
        // Process data for the previous 28 days
        const history = historyData.rows.map(row => parseFloat(row.metricValues[0].value));

        // Calculate total sales for both periods
        const totalSales = initialData.reduce((sum, row) => sum + row.sales, 0);
        const totalSalesBefore = history.reduce((sum, row) => sum + row, 0);

        // Prepare data for chart display
        const initialDataWithTotal = {
            data: [{
                name: 'Sales',
                data: initialData
                    .map(item => ({
                        x: this.convertDateString(item.date),
                        y: item.sales
                    })).sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime())
            }],
            totalSales: totalSales,
        };

        // Return the processed data with percentage change
        return {
            initialData: initialDataWithTotal,
            percentageChange: ((totalSales - totalSalesBefore) / totalSalesBefore) * 100,
            currency: currency!
        }
    }


    async getVisitors(date: number = 28): Promise<TotalSales> {
        // Query for the last 28 days
        const query1 = {
            property: `properties/${this.propertyId}`,
            dateRanges: [
                {
                    startDate: `${date}daysAgo`,
                    endDate: 'today',
                },
            ],
            dimensions: [
                {
                    name: 'date'
                }
            ],
            metrics: [
                {
                    name: 'activeUsers',
                },
            ],
        }

        // Query for the previous 28 days (29-56 days ago)
        const query2 = {
            property: `properties/${this.propertyId}`,
            dateRanges: [
                {
                    startDate: `${date*2}daysAgo`,
                    endDate: '29daysAgo',
                },
            ],
            dimensions: [
                {
                    name: 'date'
                }
            ],
            metrics: [
                {
                    name: 'activeUsers',
                },
            ],
        }

        const myCookies = useRequestHeaders(['cookie'])

        // Fetch data for both queries concurrently
        const [initialQuery, historyData] = await Promise.all([
            $fetch('/api/client/google/google', {
                headers: myCookies,
                method: 'POST',
                body: JSON.stringify(query1)
            }),
            $fetch('/api/client/google/google', {
                headers: myCookies,
                method: 'POST',
                body: JSON.stringify(query2)
            })
        ]);

        // Process data for the last 28 days
        const initialData = initialQuery.rows.map(row => ({
            date: row.dimensionValues[0].value,
            visitors: parseInt(row.metricValues[0].value),
        }));

        // Process data for the previous 28 days
        const history = historyData.rows.map(row => ({
            date: row.dimensionValues[0].value,
            visitors: parseInt(row.metricValues[0].value),
        }));

        // Calculate total sales for both periods
        const totalVisitors = initialData.reduce((sum, row) => sum + row.visitors, 0);
        const totalVisitorsBefore = history.reduce((sum, row) => sum + row.visitors, 0);

        // Prepare data for chart display
        const initialDataWithTotal = {
            data: [{
                name: 'Visitors',
                data: initialData
                    .map(item => ({
                        x: this.convertDateString(item.date),
                        y: item.visitors
                    })).sort((a, b) => new Date(a.x).getTime() - new Date(b.x).getTime())
            }],
            totalVisitors: totalVisitors,
        };

        // Return the processed data with percentage change
        return {
            initialData: initialDataWithTotal,
            percentageChange: ((totalVisitors - totalVisitorsBefore) / totalVisitorsBefore) * 100
        }
    }

    async getGoogleAdsIntegrationData(date: number = 28): Promise<TotalSales> {
        const query = {
            property: `properties/${this.propertyId}`,
            dateRanges: [
                {
                    startDate: `${date}daysAgo`,
                    endDate: 'today',
                },
            ],
            dimensions: [
                {
                    name: 'campaignName'
                },
            ],
            metrics: [
                {
                    name: 'advertiserAdCost',
                },
                {
                    name: 'totalRevenue',
                },
            ],
            dimensionFilter: {
                filter: {
                    fieldName: 'campaignName',
                    stringFilter: {
                        value: 'DK',
                        matchType: 'CONTAINS'
                    }
                },
            },
            limit: 6
        };

        const myCookies = useRequestHeaders(['cookie']);

        const response = await $fetch('/api/client/google/google', {
            headers: myCookies,
            method: 'POST',
            body: JSON.stringify(query)
        });

        // Process the response data
        const processedData = response.rows.map(row => ({
            campaign: row.dimensionValues?.[0]?.value ?? '',
            cost: parseFloat(row.metricValues?.[0]?.value ?? '0'),
            revenue: parseFloat(row.metricValues?.[1]?.value ?? '0'),
            roi: ((parseFloat(row.metricValues?.[1]?.value ?? '0') - parseFloat(row.metricValues?.[0]?.value ?? '0')) / parseFloat(row.metricValues?.[0]?.value ?? '0')) * 100,
        }));

        // Calculate totals
        const totalCost = processedData.reduce((sum, row) => sum + row.cost, 0);
        const totalRevenue = processedData.reduce((sum, row) => sum + row.revenue, 0);
        const roi = ((totalRevenue - totalCost) / totalCost) * 100;

        // Return the processed data
        return {
            totalCost: totalCost,
            totalRevenue: totalRevenue,
            roi: roi,
            processedData,
            currency: response.metadata.currencyCode
        };
    }
}